import React, { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { SettingsItem } from './SettingsItem';
import {
  creditsUsageDataAtom,
  creditsUsageLoadingAtom,
  creditsUsageErrorAtom,
  fetchCreditsUsageAtom,
} from '../../../../atoms/v3/creditsUsageAtoms';

/**
 * Credits 用量显示组件
 */
export const CreditsUsageItem: React.FC = () => {
  const { t } = useTranslation(['v3/me']);
  const [usageData] = useAtom(creditsUsageDataAtom);
  const [loading] = useAtom(creditsUsageLoadingAtom);
  const [error] = useAtom(creditsUsageErrorAtom);
  const [, fetchUsage] = useAtom(fetchCreditsUsageAtom);

  // 组件挂载时获取数据
  useEffect(() => {
    if (!usageData && !loading) {
      fetchUsage().catch(console.error);
    }
  }, [usageData, loading, fetchUsage]);

  // 计算进度条颜色
  const getProgressColor = (percentage: number): string => {
    if (percentage > 100) return 'bg-ios-red dark:bg-ios-dark-red';
    if (percentage >= 80) return 'bg-ios-orange dark:bg-ios-dark-orange';
    return 'bg-ios-green dark:bg-ios-dark-green';
  };

  // 处理重试
  const handleRetry = () => {
    fetchUsage().catch(console.error);
  };

  // 渲染进度条
  const renderProgressBar = () => {
    if (!usageData) return null;

    const percentage = Math.min((usageData.creditsUsed / usageData.creditsLimit) * 100, 100);
    const progressColor = getProgressColor(percentage);

    return (
      <div className="w-full bg-ios-gray5 dark:bg-ios-dark-gray5 rounded-full h-1.5 mt-1">
        <div 
          className={`h-1.5 rounded-full transition-all duration-300 ${progressColor}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    );
  };

  // 渲染右侧元素
  const renderRightElement = () => {
    if (loading) return null;
    
    if (error) {
      return (
        <button
          onClick={handleRetry}
          className="text-ios-blue dark:text-ios-dark-blue text-sm"
        >
          {t('v3/me:usage.retry', '重试')}
        </button>
      );
    }

    if (usageData) {
      const isOverLimit = usageData.isOverLimit;
      return (
        <span className={`text-sm ${isOverLimit ? 'text-ios-red dark:text-ios-dark-red' : 'text-ios-gray dark:text-ios-dark-gray'}`}>
          {usageData.creditsUsed} / {usageData.creditsLimit} Credits
        </span>
      );
    }

    return null;
  };

  // 渲染描述
  const renderDescription = () => {
    if (error) {
      return t('v3/me:usage.loadingFailed', '加载失败');
    }

    if (usageData) {
      return (
        <div className="space-y-1">
          <div className="text-xs text-ios-gray2 dark:text-ios-dark-gray2">
            {usageData.isOverLimit
              ? t('v3/me:usage.exceeded', { amount: usageData.creditsUsed - usageData.creditsLimit })
              : t('v3/me:usage.remainingCredits', { amount: usageData.creditsRemaining })
            }
          </div>
          {renderProgressBar()}
        </div>
      );
    }

    return null;
  };

  return (
    <SettingsItem
      icon={
        <div className="w-6 h-6 flex items-center justify-center">
          <svg
            className="w-4 h-4 text-ios-green dark:text-ios-dark-green"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
            />
          </svg>
        </div>
      }
      iconBg="bg-ios-green/10 dark:bg-ios-dark-green/20"
      title={t('v3/me:usage.monthlyUsage', '本月用量')}
      description={renderDescription()}
      rightElement={renderRightElement()}
      loading={loading}
    />
  );
};
